import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { Modal, Spinner, Button } from 'react-bootstrap'
import PaymentDetailModal from '../../Payment/components/PaymentDetailModal/index'
import { TRANSACTION_STATUS_TEXT_COLOR } from '../constants'
import { ActionContainer, StatusText, StyledModalBody, StyledTable } from '../index.styles'
import { getAmountCells, showWithdrawPopup } from '../utils'
import useDebounce from '../../../hooks/useDebounce'
import Swal from 'sweetalert2'
import { getPaymentById } from '../../../services/payment'
import { getUserTransactions } from '../../../services/wallet'

const UserTransactionModal = ({ userId, isOpen, onClose, points, onWithdrawSuccess }) => {
  const [transactions, setTransactions] = useState([])
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    hasNext: false,
  })

  const fetchTransactions = useCallback(
    async (page) => {
      setIsLoadingMore(true)
      try {
        const {
          data,
          pagination: { hasNext },
        } = await getUserTransactions(userId, page)

        setTransactions((transactions) => (page === 1 ? data : [...transactions, ...data]))
        setPagination((pagination) => ({
          ...pagination,
          hasNext: hasNext,
        }))
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoadingMore(false)
      }
    },
    [userId],
  )

  const fetchPayment = useDebounce(async (paymentId) => {
    if (!paymentId) return

    setIsLoadingPayment(true)
    try {
      const payment = await getPaymentById(paymentId)

      setSelectedPayment(payment)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'อาจเกิดจากการพรีออเดอร์',
      })

      console.error(error)
    } finally {
      setIsLoadingPayment(false)
    }
  }, 250)

  const handleRowClick = useCallback(
    async (transaction) => {
      if (!transaction.paymentId || isLoadingPayment) return

      fetchPayment(transaction.paymentId)
    },
    [fetchPayment, isLoadingPayment],
  )

  const handleClickWithdraw = useCallback(
    async (userId) => {
      await showWithdrawPopup(userId, points).then((result) => {
        if (result.isConfirmed) {
          setTimeout(() => {
            fetchTransactions(1)
            onWithdrawSuccess?.()
          }, 1000)
        }
      })
    },
    [fetchTransactions, onWithdrawSuccess, points],
  )

  useEffect(() => {
    if (isOpen) {
      fetchTransactions(pagination.page)
    }
  }, [fetchTransactions, pagination.page, isOpen])

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="lg"
      scrollable
      centered
      enforceFocus={false}
      style={{ background: 'rgba(0,0,0,0.5)' }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Transactions</Modal.Title>
      </Modal.Header>
      <StyledModalBody>
        <ActionContainer>
          <div>
            <span>แคชคงเหลือ : </span>
            <span style={{ fontWeight: '700' }}>{points}</span>
          </div>
          <Button variant="danger py-1" onClick={() => handleClickWithdraw(userId)}>
            ถอน
          </Button>
        </ActionContainer>
        <StyledTable>
          <thead>
            <tr className="text-center">
              <th>ลำดับ</th>
              <th>ใช้ / ถอน</th>
              <th>เติม / คืนจากระบบ</th>
              <th>ประเภท</th>
              <th>รหัสคูปอง</th>
              <th>สร้างเมื่อ</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {transactions.map((transaction, index) => {
              const amountCells = getAmountCells(transaction)

              return (
                <tr key={index} onClick={() => handleRowClick(transaction)}>
                  <td style={{ width: '10%' }}>{index + 1}</td>
                  {amountCells}
                  <td>
                    <StatusText color={TRANSACTION_STATUS_TEXT_COLOR[transaction.type]}>{transaction.type}</StatusText>
                  </td>
                  <td>{transaction.coupon && transaction.coupon.code}</td>
                  <td>{moment(transaction.createdAt).format('HH:mm DD/MM/YYYY')}</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            {isLoadingMore ? (
              <tr>
                <td colSpan={6} className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                </td>
              </tr>
            ) : (
              pagination.hasNext && (
                <tr>
                  <td colSpan={6} className="text-center">
                    <button
                      className="btn btn-primary p-2"
                      onClick={() => {
                        if (pagination.hasNext) {
                          setPagination((pagination) => ({
                            ...pagination,
                            page: pagination.page + 1,
                          }))
                        }
                      }}
                    >
                      โหลดเพิ่มเติม
                    </button>
                  </td>
                </tr>
              )
            )}
          </tfoot>
        </StyledTable>
      </StyledModalBody>
      <PaymentDetailModal
        payment={selectedPayment}
        setPayment={setSelectedPayment}
        onHide={() => setSelectedPayment(null)}
        isReadOnly
      />
    </Modal>
  )
}

export default UserTransactionModal
